import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PageShell from './components/PageShell';
import Snackbar from './components/Snackbar';
import {
  filterSubscriptionCustomers,
  legacyCustomerApi,
  subscriptionAdminApi,
  getApiErrorMessage,
} from '../subscriptionAdmin/api';
import { canWrite } from '../subscriptionAdmin/permissions';
import {
  ContractBillingPreview,
  formatContractBillingDisplay,
  GST_MODE_OPTIONS,
} from '../subscriptionAdmin/gstUtils';

const STATUS_TABS = [
  { key: 'pending_review', label: 'Awaiting review' },
  { key: 'payment_captured', label: 'Legacy paid' },
  { key: 'payment_pending', label: 'Legacy payment pending' },
  { key: 'contract_created', label: 'Contract created' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'all', label: 'All' },
];

const STATUS_COLORS = {
  pending_review: 'info',
  payment_pending: 'warning',
  payment_captured: 'warning',
  contract_created: 'success',
  rejected: 'error',
  cancelled: 'default',
};

const ADMIN_ACTION_STATUSES = new Set(['pending_review', 'payment_pending', 'payment_captured']);

const DIALOG_SELECT_PROPS = {
  MenuProps: {
    disablePortal: true,
    PaperProps: { sx: { maxHeight: 280 } },
  },
};

function toId(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object') return String(value._id || value.id || '');
  return String(value);
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function statusLabel(status) {
  const s = String(status || '').trim();
  if (s === 'pending_review') return 'Awaiting admin review';
  if (s === 'payment_captured') return 'Legacy — paid before contract';
  if (s === 'payment_pending') return 'Legacy — payment pending';
  if (s === 'contract_created') return 'Contract created — user pays next';
  return s.replace(/_/g, ' ') || '—';
}

function planRequestBilling(req) {
  return {
    amount: req.billingAmount,
    gstMode: req.gstMode,
    gstRate: req.gstRate,
    gstSnapshot: req.chargeSnapshot?.gstSnapshot,
  };
}

const emptyConfirmForm = () => ({
  amount: '',
  gstMode: 'exclusive',
  gstRate: 18,
  paymentChannel: 'online',
});

export default function PlanRequestsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [requests, setRequests] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmForm, setConfirmForm] = useState(emptyConfirmForm());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'error' });

  const statusFilter = STATUS_TABS[tab]?.key || 'pending_review';
  const canEdit = canWrite('contracts');

  const customerById = useMemo(() => {
    const map = new Map();
    customers.forEach((c) => map.set(toId(c._id), c));
    return map;
  }, [customers]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : undefined;
      const [requestData, userData] = await Promise.all([
        subscriptionAdminApi.listPlanRequests(params),
        legacyCustomerApi.listUsers(),
      ]);
      setRequests(Array.isArray(requestData) ? requestData : []);
      setCustomers(filterSubscriptionCustomers(userData));
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const resolveCustomer = (request) => {
    const userId = toId(request.userId);
    return customerById.get(userId) || null;
  };

  const openCreateConfirm = (request) => {
    setConfirmTarget(request);
    setConfirmForm({
      amount: request.billingAmount != null ? String(request.billingAmount) : '',
      gstMode: request.gstMode || 'exclusive',
      gstRate: request.gstRate ?? 18,
      paymentChannel: 'online',
    });
    setConfirmOpen(true);
  };

  const createContract = async () => {
    if (!confirmTarget) return;
    const id = toId(confirmTarget._id);
    setActingId(id);
    try {
      const result = await subscriptionAdminApi.createContractFromPlanRequest(id, {
        paymentChannel: confirmForm.paymentChannel,
        billing: {
          amount: Number(confirmForm.amount) || undefined,
          gstMode: confirmForm.gstMode,
          gstRate: Number(confirmForm.gstRate),
        },
      });
      const contractId = toId(result?.contract?._id);
      const contractNumber = result?.contract?.contractNumber;
      const channelNote =
        confirmForm.paymentChannel === 'offline'
          ? 'Use Activate offline or Record payment on the Contracts page.'
          : 'The user must complete Razorpay checkout before the contract becomes active.';
      setSnackbar({
        open: true,
        type: 'success',
        message: contractNumber
          ? `Contract ${contractNumber} created (${confirmForm.paymentChannel}). ${channelNote}`
          : `Contract created (${confirmForm.paymentChannel}). ${channelNote}`,
      });
      setConfirmOpen(false);
      setConfirmTarget(null);
      await load();
      if (contractId) {
        navigate('/admin-subscriptions/contracts');
      }
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    } finally {
      setActingId(null);
    }
  };

  const openReject = (request) => {
    setRejectTarget(request);
    setRejectReason('');
    setRejectOpen(true);
  };

  const rejectRequest = async () => {
    if (!rejectTarget) return;
    const id = toId(rejectTarget._id);
    setActingId(id);
    try {
      await subscriptionAdminApi.rejectPlanRequest(id, {
        reason: rejectReason.trim() || undefined,
      });
      setSnackbar({ open: true, type: 'success', message: 'Plan request rejected.' });
      setRejectOpen(false);
      setRejectTarget(null);
      load();
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    } finally {
      setActingId(null);
    }
  };

  const awaitingCount = useMemo(
    () => requests.filter((r) => r.status === 'pending_review').length,
    [requests]
  );

  return (
    <PageShell
      title="Step 2b — Plan requests"
      subtitle="User submits a plan request → admin creates contract → user pays to activate"
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        Users select a plan in the client app (no payment at selection). Review requests here, then{' '}
        <strong>Create contract</strong> to send the contract to <strong>pending payment</strong>.
        The user completes Razorpay on{' '}
        <Button
          size="small"
          onClick={() => navigate('/admin-subscriptions/contracts')}
          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
        >
          Contracts / checkout
        </Button>{' '}
        — the contract does <strong>not</strong> become active until they pay.
      </Alert>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {STATUS_TABS.map((t) => (
          <Tab key={t.key} label={t.label} />
        ))}
      </Tabs>

      {statusFilter === 'pending_review' && !loading && awaitingCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {awaitingCount} request{awaitingCount === 1 ? '' : 's'} waiting for admin review.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto', bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Requested</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Amount due</TableCell>
                <TableCell>Commitment</TableCell>
                <TableCell>Free mo.</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment ref</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#64748b' }}>
                    No plan requests for this filter.
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((req) => {
                  const customer = resolveCustomer(req);
                  const email = customer?.email || '—';
                  const busy = actingId === toId(req._id);
                  const canAct = canEdit && ADMIN_ACTION_STATUSES.has(req.status);
                  return (
                    <TableRow key={toId(req._id)} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDate(req.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{email}</Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', fontFamily: 'monospace' }}>
                          {toId(req.userId).slice(-12) || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{req.planName || '—'}</TableCell>
                      <TableCell>
                        {(() => {
                          const billing = formatContractBillingDisplay(planRequestBilling(req));
                          return (
                            <>
                              <Typography variant="body2">{billing.primary}</Typography>
                              {billing.secondary ? (
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                  {billing.secondary}
                                </Typography>
                              ) : null}
                            </>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {req.commitmentMonths != null ? `${req.commitmentMonths} mo` : '—'}
                      </TableCell>
                      <TableCell>
                        {req.freeMonthsGranted > 0 ? String(req.freeMonthsGranted) : '0'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabel(req.status)}
                          size="small"
                          color={STATUS_COLORS[req.status] || 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, maxWidth: 140 }}>
                        <Typography variant="caption" noWrap title={req.razorpay_payment_id || req.razorpay_order_id}>
                          {req.razorpay_payment_id || req.razorpay_order_id || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {req.status === 'contract_created' && req.contractId && (
                          <Button
                            size="small"
                            onClick={() => navigate('/admin-subscriptions/contracts')}
                          >
                            View contract
                          </Button>
                        )}
                        {canAct && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={busy}
                              onClick={() => openCreateConfirm(req)}
                              sx={{ mr: 1 }}
                            >
                              {busy ? 'Working…' : 'Create contract'}
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              disabled={busy}
                              onClick={() => openReject(req)}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      <Dialog open={confirmOpen} onClose={() => !actingId && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create contract from plan request?</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {confirmTarget && (
            <>
              <Typography variant="body2" sx={{ color: '#475569' }}>
                Creates a contract in <strong>pending payment</strong> for{' '}
                <strong>{resolveCustomer(confirmTarget)?.email || 'the user'}</strong> on plan{' '}
                <strong>{confirmTarget.planName || '—'}</strong>.
                {confirmTarget.freeMonthsGranted > 0
                  ? ` Intro free months: ${confirmTarget.freeMonthsGranted}.`
                  : ' No intro free month for this user.'}
              </Typography>
              <TextField
                label="Billing amount (INR, after discount)"
                type="number"
                value={confirmForm.amount}
                onChange={(e) => setConfirmForm({ ...confirmForm, amount: e.target.value })}
                helperText={
                  confirmForm.gstMode === 'inclusive'
                    ? 'Taxable value is derived from this gross amount.'
                    : 'GST is calculated on top of this amount.'
                }
                fullWidth
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <TextField
                  select
                  label="GST mode"
                  value={confirmForm.gstMode}
                  onChange={(e) => setConfirmForm({ ...confirmForm, gstMode: e.target.value })}
                  SelectProps={DIALOG_SELECT_PROPS}
                  helperText="Whether billing amount includes GST or not."
                  sx={{ flex: 1, minWidth: 200 }}
                >
                  {GST_MODE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="GST rate (%)"
                  type="number"
                  value={confirmForm.gstRate}
                  onChange={(e) => setConfirmForm({ ...confirmForm, gstRate: e.target.value })}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  sx={{ flex: 1, minWidth: 120 }}
                />
              </Box>
              <ContractBillingPreview
                amount={confirmForm.amount}
                gstMode={confirmForm.gstMode}
                gstRate={confirmForm.gstRate}
              />
              <TextField
                select
                label="Payment channel"
                value={confirmForm.paymentChannel}
                onChange={(e) => setConfirmForm({ ...confirmForm, paymentChannel: e.target.value })}
                SelectProps={DIALOG_SELECT_PROPS}
                fullWidth
                helperText={
                  confirmForm.paymentChannel === 'offline'
                    ? 'Customer pays offline — use Activate offline or Record payment on Contracts after creation.'
                    : 'Customer pays online via Razorpay contract checkout in the app.'
                }
              >
                <MenuItem value="online">Online (Razorpay)</MenuItem>
                <MenuItem value="offline">Offline (cash / bank / cheque)</MenuItem>
              </TextField>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={Boolean(actingId)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={createContract} disabled={Boolean(actingId)}>
            {actingId ? 'Creating…' : 'Create contract'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectOpen} onClose={() => !actingId && setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject plan request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#475569' }}>
            Paid requests will be refunded when possible. This cannot be undone.
          </Typography>
          <TextField
            label="Reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)} disabled={Boolean(actingId)}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={rejectRequest} disabled={Boolean(actingId)}>
            {actingId ? 'Rejecting…' : 'Reject request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.type}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      />
    </PageShell>
  );
}
